import { Component } from '@theme/component';

/**
 * Opens a YouTube/Vimeo embed (data-embed-url) or a Shopify-hosted file (data-video-src,
 * data-poster) in a native modal dialog. The iframe/video is created on open and removed on
 * close so playback stops and nothing loads before the visitor asks for it. Files play
 * muted and looping; with prefers-reduced-motion they don't autoplay (poster + controls).
 *
 * @typedef {{ dialog: HTMLDialogElement, frame: HTMLElement }} Refs
 * @extends {Component<Refs>}
 */
class DcVideoComponent extends Component {
  requiredRefs = ['dialog', 'frame'];

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    this.refs.dialog.addEventListener('close', this.#clearFrame, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /** Opens the dialog and starts the video. */
  open() {
    const { dialog, frame } = this.refs;
    if (dialog.open) return;
    const fileSrc = this.dataset.videoSrc;
    if (fileSrc) {
      if (!frame.querySelector('video')) {
        const video = document.createElement('video');
        const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
        video.src = fileSrc;
        if (this.dataset.poster) video.poster = this.dataset.poster;
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.controls = true;
        video.autoplay = !reduceMotion;
        video.setAttribute('aria-label', this.dataset.title ?? '');
        frame.classList.add('dc-video__frame--file');
        frame.append(video);
      }
      dialog.showModal();
      return;
    }

    const src = this.dataset.embedUrl;
    if (!src) return;

    if (!frame.querySelector('iframe')) {
      const iframe = document.createElement('iframe');
      iframe.src = src;
      iframe.title = this.dataset.title ?? '';
      iframe.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media';
      iframe.allowFullscreen = true;
      frame.append(iframe);
    }

    dialog.showModal();
  }

  /** Closes the dialog; focus returns to the trigger natively. */
  close() {
    this.refs.dialog.close();
  }

  /**
   * Closes when the backdrop (the dialog element itself) is clicked.
   * @param {MouseEvent} event
   */
  handleDialogClick(event) {
    if (event.target === this.refs.dialog) this.close();
  }

  #clearFrame = () => {
    this.refs.frame.replaceChildren();
  };
}

if (!customElements.get('dc-video-component')) {
  customElements.define('dc-video-component', DcVideoComponent);
}
